<template>
    <l-card-content>
        <l-headline
            :tabs="tabs"
            :title="$t('surveys.details.titleParticipantsVisibility')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            btn-test-id="surveys-headline-participants"
        >
        </l-headline>

        <hr class="visible-xs" />
        <p>
            <l-text :text="$t(`surveys.details.helpParticipants`)"></l-text>
        </p>

        <hr />

        <h4 class="color-dark tw-mb-8">
            <strong>{{ $t("surveys.details.participants.labelIncludeWho") }}</strong>
        </h4>
        <div class="row">
            <div class="col-md-4">
                <l-select
                    data-test-id="surveys-detail-participants-type"
                    :value="survey.participationRules.include.all"
                    :options="[
                        {
                            value: true,
                            text: this.$t('surveys.details.participants.optionAllEmployee'),
                        },
                        {
                            value: false,
                            text: this.$t('surveys.details.participants.optionSpecific'),
                        },
                    ]"
                    @change="
                        option => {
                            survey.participationRules.include.all =
                                option.target.value === 'true' ? true : false; // FIXME
                            updateSurvey();
                        }
                    "
                />
            </div>
            <div
                v-if="!['ONCE_BEFORE_END', 'ONCE_AFTER_START'].includes(survey.timing.frequency)"
                class="col-md-1"
            >
                <l-input-text
                    v-model="survey.participationRules.exclude.tenureInWeeksLessThan"
                    data-test-id="survey-edit-participants-min-tenure"
                    :label="``"
                    :placeholder="``"
                    @input="updateSurvey()"
                ></l-input-text>
            </div>
            <span v-if="!['ONCE_BEFORE_END', 'ONCE_AFTER_START'].includes(survey.timing.frequency)">
                {{ $t("surveys.details.labelTenureInWeeksLessThan") }}</span
            >
        </div>
        <div class="row">
            <div class="col-md-8">
                <l-multi-filter
                    v-if="!survey.participationRules.include.all"
                    :preselected-filters="survey.participationRules.include"
                    :show-custom-attributes="true"
                    @update="updateFiltersInclude"
                />
            </div>
        </div>

        <hr />

        <p>{{ $t("surveys.details.participants.participationIncludeHelp") }}</p>

        <hr />

        <h4 class="color-dark tw-mb-8">
            <strong>{{ $t("surveys.details.participants.labelExcludeWho") }}</strong>
        </h4>
        <div class="row">
            <div class="col-md-8">
                <l-multi-filter
                    :preselected-filters="survey.participationRules.exclude"
                    :show-custom-attributes="true"
                    @update="updateFiltersExclude"
                />
            </div>
        </div>

        <hr />

        <p>{{ $t("surveys.details.participants.participationExcludeHelp") }}</p>

        <hr />

        <div class="row">
            <div class="col-md-5">
                <div
                    class="togglebutton anonymityToggle"
                    data-test-id="survey-edit-participants-not-anon"
                >
                    <label
                        v-b-popover.hover.top="$t('surveys.details.notAnonymousHelp')"
                        for="notAnonymous"
                    >
                        <input
                            id="notAnonymous"
                            v-model="survey.settings.notAnonymous"
                            type="checkbox"
                            :disabled="
                                Boolean(survey.periods.length) &&
                                    !['ONCE_AFTER_START', 'ONCE_BEFORE_END'].includes(
                                        survey.timing.frequency,
                                    )
                            "
                            @change="updateSurvey()"
                        />
                        <span class="toggle"></span>
                        <span class="color-dark">{{
                            $t("surveys.details.labelNotAnonymous")
                        }}</span>
                    </label>
                </div>
            </div>
            <div class="col-md-5">
                <div
                    class="togglebutton anonymityToggle"
                    data-test-id="survey-edit-participants-not-anon"
                >
                    <label
                        v-b-popover.hover.top="$t('surveys.details.fullExportEnabledHelp')"
                        for="fullExportEnabled"
                    >
                        <input
                            id="fullExportEnabled"
                            v-model="survey.settings.fullExportEnabled"
                            type="checkbox"
                            :disabled="
                                Boolean(survey.periods.length) &&
                                    !['ONCE_AFTER_START', 'ONCE_BEFORE_END'].includes(
                                        survey.timing.frequency,
                                    )
                            "
                            @change="updateSurvey()"
                        />
                        <span class="toggle"></span>
                        <span class="color-dark">{{
                            $t("surveys.details.labelFullExportEnabled")
                        }}</span>
                    </label>
                </div>
            </div>
            <div class="col-md-2">
                <l-input-text
                    v-show="!survey.settings.notAnonymous"
                    v-model="survey.settings.anonymityThreshold"
                    v-b-popover.hover.top="$t('surveys.details.anonymityThresholdHelp')"
                    data-test-id="survey-edit-participants-anonymity-threshold"
                    style="margin-top: 30px;"
                    :label="$t('surveys.details.labelAnonymityThreshold')"
                    :placeholder="``"
                    :disabled="
                        Boolean(survey.periods.length) &&
                            !['ONCE_AFTER_START', 'ONCE_BEFORE_END'].includes(
                                survey.timing.frequency,
                            )
                    "
                    @input="updateSurvey()"
                ></l-input-text>
            </div>
        </div>

        <div v-if="survey.status != 'closed'" class="row">
            <hr />
            <div v-if="error && error.message">
                <div id="error" class="alert alert-danger">
                    <span>{{ error.message }}</span>
                </div>
            </div>

            <div class="row">
                <div class="col-md-4 col-md-offset-5 col-sm-6">
                    <button
                        v-if="
                            survey.periods.length &&
                                !['ONCE_AFTER_START', 'ONCE_BEFORE_END'].includes(
                                    survey.timing.frequency,
                                )
                        "
                        v-b-popover.hover.top="{
                            content: $t(`surveys.details.participants.labelAddNewUserHelp`),
                        }"
                        class="btn btn-raised btn-block"
                        @click="openModal"
                    >
                        <span> {{ $t("surveys.details.participants.labelAddNewUser") }}</span>
                    </button>
                </div>
                <div class="col-md-3 col-sm-6">
                    <button
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        @click="$router.push(`/surveys/edit/${$route.params.id}/notifications`)"
                    >
                        <span>{{ $t("surveys.details.btnSaveChanges") }}</span>
                    </button>
                </div>
            </div>
        </div>
        <l-modal v-if="showModal" size="m" :close-on-backdrop-click="false" @close="goBack">
            <template #header> {{ $t("surveys.sendConfirmModal.title") }} </template>
            <template #body>
                <div class="panel-body">
                    <div class="row">
                        <div class="col-md-12">
                            <span class="ng-scope">{{
                                $t("surveys.participate.modal.uninvitedDescription")
                            }}</span>
                            <br />
                            <br />
                            <ul>
                                <li
                                    v-for="user in uninvited.slice(0, maxUninvitedUserNum)"
                                    :key="user._id"
                                >
                                    {{ user.firstname }} {{ user.lastname }}
                                </li>
                                <li v-if="uninvited.length > maxUninvitedUserNum">
                                    ... and
                                    <strong>{{ uninvited.length - maxUninvitedUserNum }}</strong>
                                    more.
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div class="footer">
                    <button class="btn btn-default" type="button" @click="showModal = false">
                        <span>{{ $t("surveys.participate.modal.cancel") }}</span>
                    </button>
                    <button class="btn btn-primary btn-raised" type="button" @click="confirmInvite">
                        <span>{{ $t("surveys.participate.modal.confirm") }}</span>
                    </button>
                </div>
            </template>
        </l-modal>
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LText from "@/components/LText";
import LModal from "@/components/LModal";

import icon from "@/img/icons/surveys.png";
import LMultiFilter from "@/components/LMultiFilter.vue";
import LSelect from "@/components/LSelect.vue";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        LText,
        LModal,
        LMultiFilter,
        LSelect,
    },
    props: {
        autoSavingText: {
            type: String,
            default: "",
        },
        openHelpHC: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            maxUninvitedUserNum: 50,
            showModal: false,
            survey: {},
        };
    },

    computed: {
        ...mapGetters({
            user: "users/current/user",

            getDetail: "surveys/surveys/detail",
            uninvited: "surveys/surveys/uninvited",

            error: "surveys/surveys/error",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
            },
        },
    },
    created() {
        this.icon = icon;
    },
    methods: {
        updateFiltersInclude(filters) {
            this.$set(this.survey.participationRules, "include", {
                all: this.survey.participationRules.include.all,
                localTeams: filters.localTeams,
                users: filters.users,
                locations: filters.locations,
                customAttributes: filters.customAttributes,
            });
            this.updateSurvey();
        },
        updateFiltersExclude(filters) {
            this.$set(this.survey.participationRules, "exclude", {
                localTeams: filters.localTeams,
                users: filters.users,
                locations: filters.locations,
                customAttributes: filters.customAttributes,
            });
            this.updateSurvey();
        },
        updateSurvey() {
            this.$emit("change", "participants", this.survey);
        },
        openModal() {
            this.$store
                .dispatch("surveys/surveys/getUninvited", {
                    payload: { surveyId: this.survey._id },
                })
                .then(() => {
                    if (this.uninvited?.length > 0) {
                        this.showModal = true;
                    }
                });
        },
        confirmInvite() {
            this.$store.dispatch("surveys/surveys/invite", {
                payload: { surveyId: this.survey._id },
            });
            this.showModal = false;
        },
        goBack() {
            this.showModal = false;
        },
    },
};
</script>

<style scoped>
.anonymityToggle {
    margin-top: 30px;
    margin-bottom: 40px;
}
</style>
